var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useLayoutEffect, useReducer, useRef, useState, useEffect, useId, useCallback, } from 'react';
import classNames from 'classnames';
import { OptionBody } from './OptionBody';
import { createReducer } from './reducer';
import { getListToRender } from '../getListToRender';
import { scrollIntoView } from '../scrollIntoView';
import { getNewHighlightedIndexUp, getNewHighlightedIndexDown, } from '../getNewHighlightedIndex';
import { setArrowAllyMessage, useSetAllyMessageItemSelection } from '../a11y';
import { Results } from '../Results';
import { mergeRefs } from '../mergeRefs';
import { fixedForwardRef } from '../fixedForwardRef';
import { addFlagOnEventHandler } from '../addFlagOnEventHandler';
import { useHandleContainerFocus } from '../useHandleContainerFocus';
import { useIsExpandedCallbacks } from '../useIsExpandedCallbacks';
import { useRefs } from '../useRefs';
import { ToggleButton } from '../ToggleButton';
import { ListBox } from '../ListBox';
import isDeepEqual from 'lodash.isequal';
var ARROW_UP = 'ArrowUp';
var ARROW_DOWN = 'ArrowDown';
var ESCAPE = 'Escape';
var ENTER = 'Enter';
var TAB = 'Tab';
function SearchableDropdownWithForwardRef(_a, ref) {
    var _b, _c, _d, _e, _f, _g, _h;
    var id = _a.id, labelledById = _a.labelledById, className = _a.className, dropdownList = _a.dropdownList, dropdownAttributes = _a.dropdownAttributes, searchAttributes = _a.searchAttributes, _j = _a.displayAttribute, displayAttribute = _j === void 0 ? searchAttributes[0] : _j, _k = _a.maxRenderedDropdownElements, maxRenderedDropdownElements = _k === void 0 ? Number.MAX_SAFE_INTEGER : _k, onChange = _a.onChange, inputProps = _a.inputProps, CustomOptionBody = _a.optionBody, postListElement = _a.postListElement, noMatch = _a.noMatch, _l = _a.locale, locale = _l === void 0 ? 'nb' : _l, ariaInvalid = _a.ariaInvalid, _m = _a.formatter, formatter = _m === void 0 ? function (value) { return value; } : _m, searchMatcher = _a.searchMatcher, selectedItem = _a.selectedItem, _o = _a.isLoading, isLoading = _o === void 0 ? false : _o, onOpen = _a.onOpen, onClose = _a.onClose, _p = _a.isEqual, isEqual = _p === void 0 ? isDeepEqual : _p, rest = __rest(_a, ["id", "labelledById", "className", "dropdownList", "dropdownAttributes", "searchAttributes", "displayAttribute", "maxRenderedDropdownElements", "onChange", "inputProps", "optionBody", "postListElement", "noMatch", "locale", "ariaInvalid", "formatter", "searchMatcher", "selectedItem", "isLoading", "onOpen", "onClose", "isEqual"]);
    var _q = useReducer(createReducer({
        dropdownList: dropdownList,
        displayAttribute: displayAttribute,
        searchAttributes: searchAttributes,
        maxRenderedDropdownElements: maxRenderedDropdownElements,
        noMatchDropdownList: noMatch === null || noMatch === void 0 ? void 0 : noMatch.dropdownList,
        searchMatcher: searchMatcher,
        onChange: onChange,
    }), {
        isExpanded: false,
        selectedItems: [],
        highlightedIndex: -1,
        formattedInputValue: '',
        inputValue: selectedItem ? selectedItem[displayAttribute] : '',
    }, function (initialState) {
        return __assign(__assign({}, initialState), getListToRender({
            inputValue: initialState.inputValue,
            searchAttributes: searchAttributes,
            maxRenderedDropdownElements: maxRenderedDropdownElements,
            dropdownList: dropdownList,
            noMatchDropdownList: noMatch === null || noMatch === void 0 ? void 0 : noMatch.dropdownList,
            searchMatcher: searchMatcher,
            showAllItemsInDropdown: !!selectedItem,
        }));
    }), state = _q[0], dispatch = _q[1];
    var refs = useRefs({ listToRender: state.listToRender });
    var _r = useState(false), hasFocus = _r[0], setHasFocus = _r[1];
    var inputRef = useRef(null);
    var containerRef = useRef(null);
    var ListItemBodyElement = CustomOptionBody || OptionBody;
    var listBoxRef = useRef(null);
    var noMatchMessageId = useId();
    var shouldFocusInput = useRef(false);
    var handleInputClick = function () {
        dispatch({ type: 'InputClick' });
    };
    var handleInputBlur = function (e) {
        if (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onBlur) {
            inputProps.onBlur(e);
        }
    };
    useEffect(function () {
        dispatch({
            type: 'ItemSelectedProgrammatically',
            payload: { selectedItem: selectedItem },
        });
    }, [selectedItem, dispatch]);
    useSetAllyMessageItemSelection({
        hasFocus: hasFocus,
        isExpanded: state.isExpanded,
        isLoading: isLoading,
        locale: locale,
        resultCount: state.listToRender.length,
        selectedValue: (_b = state.selectedItem) === null || _b === void 0 ? void 0 : _b[displayAttribute],
    });
    useLayoutEffect(function () {
        var _a;
        if (shouldFocusInput.current) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            shouldFocusInput.current = false;
        }
    });
    useEffect(function () {
        dispatch({
            type: 'DropdownListPropUpdated',
        });
    }, [dropdownList, dispatch]);
    useIsExpandedCallbacks({ isExpanded: state.isExpanded, onClose: onClose, onOpen: onOpen });
    var handleFocusMovedOutside = useCallback(function () {
        return dispatch({
            type: 'FocusMovedOutSide',
        });
    }, []);
    useHandleContainerFocus({
        id: id,
        containerRef: containerRef,
        handleFocusMovedOutside: handleFocusMovedOutside,
    });
    var handleKeyDown = function (event) {
        if (event.key === ENTER && state.highlightedIndex >= 0) {
            event.preventDefault();
            dispatch({
                type: 'InputKeyDownEnter',
                payload: {
                    selectedItem: state.listToRender[state.highlightedIndex],
                },
            });
            onChange === null || onChange === void 0 ? void 0 : onChange(state.listToRender[state.highlightedIndex]);
            return;
        }
        else if (event.key === ESCAPE) {
            dispatch({
                type: 'InputKeyDownEscape',
            });
            return;
        }
        if (event.key === ARROW_UP) {
            event.preventDefault();
            if (state.listToRender.length) {
                var newHighlightedIndex = getNewHighlightedIndexUp(state.highlightedIndex, state.listToRender.length);
                dispatch({
                    type: 'InputKeyDownArrowUp',
                    payload: { highlightedIndex: newHighlightedIndex },
                });
                if (newHighlightedIndex >= 0) {
                    setArrowAllyMessage(state === null || state === void 0 ? void 0 : state.listToRender[newHighlightedIndex], dropdownAttributes);
                }
                scrollIntoView(refs[newHighlightedIndex].current, listBoxRef.current);
            }
            return;
        }
        if (event.key === ARROW_DOWN) {
            event.preventDefault();
            if (state.listToRender.length) {
                var newHighlightedIndex = getNewHighlightedIndexDown(state.highlightedIndex, state.listToRender.length);
                dispatch({
                    type: 'InputKeyDownArrowDown',
                    payload: { highlightedIndex: newHighlightedIndex },
                });
                if (newHighlightedIndex >= 0) {
                    setArrowAllyMessage(state === null || state === void 0 ? void 0 : state.listToRender[newHighlightedIndex], dropdownAttributes);
                }
                scrollIntoView(refs[newHighlightedIndex].current, listBoxRef.current);
            }
        }
        if (event.key === TAB) {
            dispatch({ type: 'FocusMovedOutSide' });
        }
    };
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React.createElement("div", { onKeyDown: handleKeyDown, className: classNames(className, 'ffe-searchable-dropdown', 'ffe-default-mode'), ref: containerRef, onMouseDown: addFlagOnEventHandler(id), onFocus: addFlagOnEventHandler(id) },
        React.createElement("div", { className: "ffe-searchable-dropdown__input", onClick: function () {
                var _a;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            } },
            React.createElement("input", __assign({}, inputProps, { ref: mergeRefs([inputRef, ref]), id: id, "aria-labelledby": labelledById, onClick: handleInputClick, onChange: function (e) {
                    if (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onChange) {
                        inputProps.onChange(e);
                    }
                    dispatch({
                        type: 'InputChange',
                        payload: { inputValue: e.target.value },
                    });
                }, onFocus: function () {
                    setHasFocus(true);
                    dispatch({ type: 'InputClick' });
                }, onBlur: handleInputBlur, "aria-describedby": [
                    inputProps === null || inputProps === void 0 ? void 0 : inputProps['aria-describedby'],
                    state.noMatch && noMatchMessageId,
                ]
                    .filter(Boolean)
                    .join(' ') || undefined, value: formatter(state.inputValue), type: "text", role: "combobox", autoComplete: "off", "aria-controls": (_d = (_c = listBoxRef.current) === null || _c === void 0 ? void 0 : _c.getAttribute('id')) !== null && _d !== void 0 ? _d : undefined, "aria-expanded": state.isExpanded && !!state.listToRender.length, "aria-autocomplete": "list", "aria-haspopup": "listbox", "aria-activedescendant": state.highlightedIndex >= 0
                    ? ((_g = (_f = (_e = refs[state.highlightedIndex]) === null || _e === void 0 ? void 0 : _e.current) === null || _f === void 0 ? void 0 : _f.getAttribute('id')) !== null && _g !== void 0 ? _g : undefined)
                    : undefined, "aria-invalid": (_h = rest['aria-invalid']) !== null && _h !== void 0 ? _h : ariaInvalid }))),
        React.createElement(ToggleButton, { isExpanded: state.isExpanded, onClick: function () {
                dispatch({
                    type: 'ToggleButtonPressed',
                });
            }, isLoading: isLoading }),
        React.createElement(ListBox, { ref: listBoxRef, isExpanded: state.isExpanded },
            state.isExpanded && (React.createElement(Results, { isEqual: isEqual, listToRender: state.listToRender, OptionBody: ListItemBodyElement, highlightedIndex: state.highlightedIndex, dropdownAttributes: dropdownAttributes, locale: locale, refs: refs, onChange: function (item) {
                    dispatch({
                        type: 'ItemOnClick',
                        payload: { selectedItem: item },
                    });
                    onChange === null || onChange === void 0 ? void 0 : onChange(item);
                }, noMatch: state.noMatch ? noMatch : undefined, noMatchMessageId: noMatchMessageId, selectedItems: state.selectedItem ? [state.selectedItem] : [] })),
            postListElement && (React.createElement("div", { className: "ffe-searchable-dropdown__list--post-list-element" }, postListElement)))));
}
export var SearchableDropdown = fixedForwardRef(SearchableDropdownWithForwardRef);
